import { useMutation } from "@apollo/client";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DeleteIcon from "@mui/icons-material/Delete";
import DrawIcon from "@mui/icons-material/Draw";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SendIcon from "@mui/icons-material/Send";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  BulkAddReviewersDialog,
  BulkSetSignerDialog,
  BulkActionSubmission,
} from "components/BulkActions";
import { ChangeEncampStatusDialog } from "components/Submission/ChangeEncampStatusDialog";
import { VALID_ENCAMP_STATUSES } from "encamp-shared/src/submissions";
import { SubmissionMaterializationType } from "generated-graphql/graphql";
import { useState, useCallback, useMemo } from "react";

import {
  SET_SUBMISSION_ASSIGNEES_STAFF_MUTATION,
  CLEAR_SUBMISSION_ASSIGNEES_MUTATION,
  RESET_MATERIALIZED_SUBMISSIONS_MUTATION,
} from "./api";
import { AssignDialog } from "./AssignDialog";
import { ChangeDueDateDialog } from "./ChangeDueDateDialog";
import { ChangeStatusDialog } from "./ChangeStatusDialog";
import { Row } from "./columns";
import { DeleteSubmissionsDialog } from "./DeleteSubmissionsDialog";
import { SendToReviewersDialog } from "./SendToReviewersDialog";
import { isResettable } from "./utils";

/**
 * Transforms Waste Fulfillment grid Row to common BulkActionSubmission format.
 */
function toBulkActionSubmission(row: Row): BulkActionSubmission {
  return {
    id: row.id,
    submissionId: row.submissionId ?? null,
    isVirtualEvent: row.materializationType === SubmissionMaterializationType.VirtualEvent,
    status: row.submissionStatus ?? null,
    dueDate: row.deadline ?? null,
    facilityName: row.facilities?.map((f) => f.name).join(", ") || row.facilityName || "",
    submissionType: row.submissionType ?? null,
    title: row.title ?? null,
    reviewerCount: 0,
    tenantId: row.tenantId ?? null,
  };
}

interface BulkActionsMenuProps {
  selectedRows: Row[];
  selectedIds: string[];
  /** IDs of rows that have materialized submissions */
  selectedSubmissionIds: string[];
  /** IDs of virtual events (not yet materialized) */
  selectedVirtualEventIds: string[];
  onSuccess: () => void;
  onClearSelection: () => void;
}

export function BulkActionsMenu({
  selectedRows,
  selectedIds,
  selectedSubmissionIds,
  selectedVirtualEventIds,
  onSuccess,
  onClearSelection,
}: BulkActionsMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [assignOwnerDialogOpen, setAssignOwnerDialogOpen] = useState(false);
  const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false);
  const [changeDueDateDialogOpen, setChangeDueDateDialogOpen] = useState(false);
  const [sendToReviewersDialogOpen, setSendToReviewersDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [bulkAddReviewersDialogOpen, setBulkAddReviewersDialogOpen] = useState(false);
  const [bulkSetSignerDialogOpen, setBulkSetSignerDialogOpen] = useState(false);
  const [changeEncampStatusDialogOpen, setChangeEncampStatusDialogOpen] = useState(false);

  const { success, error } = useAlerts();

  const open = Boolean(anchorEl);
  const hasVirtualEvents = selectedVirtualEventIds.length > 0;
  const hasMaterializedSubmissions = selectedSubmissionIds.length > 0;

  // Resettable submissions are those with ONCE tasks (materialized from recurring series)
  const resettableRows = selectedRows.filter(isResettable);
  const resettableSubmissionIds = resettableRows
    .map((r) => r.submissionId)
    .filter((id): id is string => id != null);
  const hasResettableSubmissions = resettableSubmissionIds.length > 0;
  const hasNonResettableSelected = selectedSubmissionIds.length > resettableSubmissionIds.length;

  const isMultiTenant = useMemo(() => {
    const tenantIds = new Set(
      selectedRows.map((r) => r.tenantId).filter((id): id is string => Boolean(id))
    );
    return tenantIds.size > 1;
  }, [selectedRows]);

  const { canChangeEncampStatus, encampStatusSubmissions } = useMemo(() => {
    const materialized = selectedRows.filter(
      (r) =>
        r.submissionId != null &&
        r.materializationType !== SubmissionMaterializationType.VirtualEvent
    );
    if (materialized.length === 0) {
      return { canChangeEncampStatus: false, encampStatusSubmissions: [] };
    }
    const statuses = new Set(materialized.map((r) => r.submissionStatus));
    if (statuses.size !== 1) {
      return { canChangeEncampStatus: false, encampStatusSubmissions: [] };
    }
    const commonStatus = [...statuses][0];
    const hasValidSubStatuses =
      !!commonStatus && (VALID_ENCAMP_STATUSES[commonStatus]?.length ?? 0) > 0;
    return {
      canChangeEncampStatus: hasValidSubStatuses,
      encampStatusSubmissions: materialized.flatMap((r) => {
        if (!r.submissionId) return [];
        return [
          {
            submissionId: r.submissionId,
            submissionStatus: r.submissionStatus ?? "",
            encampStatus: r.submissionEncampStatus,
          },
        ];
      }),
    };
  }, [selectedRows]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Combined handler for successful bulk actions: refetch data and clear selection
  const handleBulkActionSuccess = useCallback(() => {
    onSuccess();
    onClearSelection();
  }, [onSuccess, onClearSelection]);

  // Note: For inline mutation handlers below, use handleBulkActionSuccess
  // For dialogs, pass handleBulkActionSuccess as the onSuccess prop

  // All mutations include refetchQueries to ensure the grid updates after changes
  const [setAssignees, { loading: assigneesLoading }] = useMutation(
    SET_SUBMISSION_ASSIGNEES_STAFF_MUTATION,
    {
      refetchQueries: ["WasteSubmissionsForStaff", "WasteSubmissionsStaffSummary"],
      awaitRefetchQueries: true,
    }
  );
  const [clearAssignees, { loading: clearAssigneesLoading }] = useMutation(
    CLEAR_SUBMISSION_ASSIGNEES_MUTATION,
    {
      refetchQueries: ["WasteSubmissionsForStaff", "WasteSubmissionsStaffSummary"],
      awaitRefetchQueries: true,
    }
  );
  const [resetMaterializedSubmissions, { loading: resetLoading }] = useMutation(
    RESET_MATERIALIZED_SUBMISSIONS_MUTATION,
    {
      refetchQueries: ["WasteSubmissionsForStaff", "WasteSubmissionsStaffSummary"],
      awaitRefetchQueries: true,
    }
  );

  const handleAssignOwner = async (userId: string) => {
    try {
      const result = await setAssignees({
        variables: {
          submissionIds: selectedSubmissionIds,
          virtualEventIds: selectedVirtualEventIds.length > 0 ? selectedVirtualEventIds : undefined,
          assigneeId: userId,
        },
      });
      if (result.data?.setSubmissionAssignees?.succeeded) {
        const updated = result.data.setSubmissionAssignees.updated ?? 0;
        success(`Assigned Encamp assignee to ${updated} submissions`);
        setAssignOwnerDialogOpen(false);
        handleBulkActionSuccess();
      }
    } catch (e) {
      error("Failed to assign Encamp assignee");
    }
  };

  const handleClearOwner = async () => {
    if (!hasMaterializedSubmissions) {
      return;
    }

    try {
      const result = await clearAssignees({
        variables: {
          submissionIds: selectedSubmissionIds,
        },
      });
      if (result.data?.clearSubmissionAssignees?.succeeded) {
        const updated = result.data.clearSubmissionAssignees.updated ?? 0;
        success(`Cleared Encamp assignee from ${updated} submissions`);
        handleClose();
        handleBulkActionSuccess();
      }
    } catch (e) {
      error("Failed to clear Encamp assignee");
    }
  };

  const handleResetSubmissions = async () => {
    if (!hasResettableSubmissions) {
      return;
    }

    try {
      const result = await resetMaterializedSubmissions({
        variables: {
          submissionIds: resettableSubmissionIds,
        },
      });
      const data = result.data?.resetMaterializedSubmissions;
      if (data) {
        const resetCount = data.reset ?? 0;
        const skippedCount = data.skipped ?? 0;

        if (resetCount > 0 && skippedCount > 0) {
          success(
            `Reset ${resetCount} submissions (${skippedCount} skipped - not part of a recurring series)`
          );
        } else if (resetCount > 0) {
          success(`Reset ${resetCount} submissions to virtual events`);
        } else if (skippedCount > 0) {
          // All items were skipped - show error
          error(
            `Could not reset ${skippedCount} submission(s) - they may not be part of a recurring series`
          );
        }
        setResetDialogOpen(false);
        handleBulkActionSuccess();
      }
    } catch (e) {
      error("Failed to reset submissions");
      setResetDialogOpen(false);
    }
  };

  const isLoading = assigneesLoading || clearAssigneesLoading || resetLoading;

  // Build button label
  let buttonLabel = `Bulk Actions (${selectedIds.length})`;
  if (hasVirtualEvents && hasMaterializedSubmissions) {
    buttonLabel = `Bulk Actions (${selectedSubmissionIds.length} + ${selectedVirtualEventIds.length} virtual)`;
  } else if (hasVirtualEvents) {
    buttonLabel = `Bulk Actions (${selectedVirtualEventIds.length} virtual)`;
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        endIcon={<ArrowDownIcon />}
        onClick={handleClick}
        disabled={selectedIds.length === 0 || isLoading}
      >
        {buttonLabel}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        {hasVirtualEvents && [
          <MenuItem key="virtual-info" disabled sx={{ opacity: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Virtual events will be started when assigned
            </Typography>
          </MenuItem>,
          <Divider key="virtual-divider" />,
        ]}

        {/* Status & Workflow Actions */}
        <MenuItem
          onClick={() => {
            handleClose();
            setChangeStatusDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Status...</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setChangeEncampStatusDialogOpen(true);
          }}
          disabled={!canChangeEncampStatus}
        >
          <ListItemIcon>
            <TuneIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Encamp Status...</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setChangeDueDateDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <CalendarMonthIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Due Date...</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setSendToReviewersDialogOpen(true);
          }}
          disabled={!hasMaterializedSubmissions}
        >
          <ListItemIcon>
            <SendIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Send to Reviewers...</ListItemText>
        </MenuItem>

        <Divider />

        {/* Reviewer & Signer Actions */}
        <Tooltip
          title={isMultiTenant ? "Select submissions from a single tenant to add reviewers" : ""}
          placement="right"
        >
          <span>
            <MenuItem
              onClick={() => {
                handleClose();
                setBulkAddReviewersDialogOpen(true);
              }}
              disabled={!hasMaterializedSubmissions || isMultiTenant}
            >
              <ListItemIcon>
                <GroupAddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add Reviewers...</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip
          title={isMultiTenant ? "Select submissions from a single tenant to set a signer" : ""}
          placement="right"
        >
          <span>
            <MenuItem
              onClick={() => {
                handleClose();
                setBulkSetSignerDialogOpen(true);
              }}
              disabled={!hasMaterializedSubmissions || isMultiTenant}
            >
              <ListItemIcon>
                <DrawIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Set Signer...</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>

        <Divider />

        {/* Staff Assignment Actions */}
        <MenuItem
          onClick={() => {
            handleClose();
            setAssignOwnerDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Assign Encamp Assignee...</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleClearOwner} disabled={isLoading || !hasMaterializedSubmissions}>
          <ListItemIcon>
            <PersonOffIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Clear Encamp Assignee</ListItemText>
        </MenuItem>
        <Divider />

        {/* Destructive Actions */}
        <MenuItem
          onClick={() => {
            handleClose();
            setResetDialogOpen(true);
          }}
          disabled={isLoading || !hasResettableSubmissions}
          sx={{ color: "warning.main" }}
        >
          <ListItemIcon>
            <RestartAltIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText
            primary="Reset to Virtual Event..."
            secondary={
              hasVirtualEvents
                ? "Only available for materialized instances"
                : hasNonResettableSelected
                  ? `${resettableSubmissionIds.length} of ${selectedSubmissionIds.length} can be reset`
                  : undefined
            }
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setDeleteDialogOpen(true);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Selected...</ListItemText>
        </MenuItem>
      </Menu>

      {/* Staff Assignment Dialogs */}
      <AssignDialog
        open={assignOwnerDialogOpen}
        onClose={() => setAssignOwnerDialogOpen(false)}
        onAssign={handleAssignOwner}
        title="Assign Encamp Assignee"
        selectedCount={selectedIds.length}
        isLoading={assigneesLoading}
        warningMessage={
          hasVirtualEvents
            ? `${selectedVirtualEventIds.length} virtual event(s) will be started`
            : undefined
        }
      />

      {/* Status & Workflow Dialogs */}
      <ChangeStatusDialog
        open={changeStatusDialogOpen}
        onClose={() => setChangeStatusDialogOpen(false)}
        submissions={selectedRows}
        onSuccess={handleBulkActionSuccess}
      />

      <ChangeEncampStatusDialog
        open={changeEncampStatusDialogOpen}
        onClose={() => setChangeEncampStatusDialogOpen(false)}
        submissions={encampStatusSubmissions}
        onSuccess={handleBulkActionSuccess}
      />

      <ChangeDueDateDialog
        open={changeDueDateDialogOpen}
        onClose={() => setChangeDueDateDialogOpen(false)}
        submissions={selectedRows}
        onSuccess={handleBulkActionSuccess}
      />

      <SendToReviewersDialog
        open={sendToReviewersDialogOpen}
        onClose={() => setSendToReviewersDialogOpen(false)}
        submissions={selectedRows}
        onSuccess={handleBulkActionSuccess}
      />

      <DeleteSubmissionsDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        submissions={selectedRows}
        onSuccess={handleBulkActionSuccess}
      />

      {/* Customer Reviewer Dialogs */}
      <BulkAddReviewersDialog
        open={bulkAddReviewersDialogOpen}
        onClose={() => setBulkAddReviewersDialogOpen(false)}
        submissions={selectedRows.map(toBulkActionSubmission)}
        onSuccess={handleBulkActionSuccess}
        refetchQueries={["WasteSubmissionsForStaff", "WasteSubmissionsStaffSummary"]}
      />

      <BulkSetSignerDialog
        open={bulkSetSignerDialogOpen}
        onClose={() => setBulkSetSignerDialogOpen(false)}
        submissions={selectedRows.map(toBulkActionSubmission)}
        onSuccess={handleBulkActionSuccess}
        refetchQueries={["WasteSubmissionsForStaff", "WasteSubmissionsStaffSummary"]}
      />

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset Submissions to Virtual Events</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will reset {resettableSubmissionIds.length} materialized submission(s) back to
            virtual events. The submissions will be deleted and will reappear in the series as
            not-started virtual events.
          </DialogContentText>
          {hasNonResettableSelected && (
            <DialogContentText sx={{ mt: 1, color: "text.secondary" }}>
              Note: {selectedSubmissionIds.length - resettableSubmissionIds.length} selected item(s)
              are template submissions and cannot be reset.
            </DialogContentText>
          )}
          <DialogContentText sx={{ mt: 2, fontWeight: "bold" }}>
            This action cannot be undone. Any data entered in these submissions will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleResetSubmissions}
            color="warning"
            variant="contained"
            disabled={resetLoading}
          >
            {resetLoading ? "Resetting..." : "Reset Submissions"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
